import { useState, useEffect, useCallback } from "react";
import { startOfWeek, endOfWeek } from "date-fns";
import DateRangePicker from "@/components/DateRangePicker";
import KPICards from "@/components/KPICards";
import AssignedToChart from "@/components/AssignedToChart";
import CategoryChart from "@/components/CategoryChart";
import PriorityChart from "@/components/PriorityChart";
import StatusDonut from "@/components/StatusDonut";
import TicketTrendChart from "@/components/TicketTrendChart";
import RecentTickets from "@/components/RecentTickets";
import SLAAlerts from "@/components/SLAAlerts";
import QualityScoreWidget from "@/components/QualityScoreWidget";
import SurveyStatsWidget from "@/components/SurveyStatsWidget";
import api from "@/lib/api";

// Get current work week (Monday-Friday)
const getCurrentWorkWeek = () => {
  const now = new Date();
  const monday = startOfWeek(now, { weekStartsOn: 1 }); // 1 = Monday
  const friday = endOfWeek(now, { weekStartsOn: 1 });
  friday.setDate(friday.getDate() - 2); // Adjust to Friday (endOfWeek gives Sunday)
  return { from: monday, to: friday };
};

// Load date range from localStorage or default to current work week
const loadDateRange = () => {
  const saved = localStorage.getItem("dashboardDateRange");
  if (saved) {
    try {
      const parsed = JSON.parse(saved);
      return {
        from: parsed.from ? new Date(parsed.from) : undefined,
        to: parsed.to ? new Date(parsed.to) : undefined,
        isDefault: parsed.isDefault ?? false
      };
    } catch (e) {
      console.error("Failed to parse saved date range", e);
    }
  }
  // Default to current work week
  return { ...getCurrentWorkWeek(), isDefault: true };
};

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState(loadDateRange);

  // Handle date range changes and persist to localStorage
  const handleDateChange = useCallback((newRange) => {
    const isDefault = false; // User changed it, so not default anymore
    const rangeWithFlag = { ...newRange, isDefault };
    setDateRange(rangeWithFlag);
    
    // Persist to localStorage
    localStorage.setItem("dashboardDateRange", JSON.stringify({
      from: newRange.from?.toISOString(),
      to: newRange.to?.toISOString(),
      isDefault
    }));
  }, []);

  // Reset to current work week
  const handleResetToWorkWeek = useCallback(() => {
    const workWeek = getCurrentWorkWeek();
    const rangeWithFlag = { ...workWeek, isDefault: true };
    setDateRange(rangeWithFlag);
    
    // Persist to localStorage
    localStorage.setItem("dashboardDateRange", JSON.stringify({
      from: workWeek.from?.toISOString(),
      to: workWeek.to?.toISOString(),
      isDefault: true
    }));
  }, []);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (dateRange?.from) {
        params.date_from = dateRange.from.toISOString();
      }
      if (dateRange?.to) {
        params.date_to = dateRange.to.toISOString();
      }
      const res = await api.getTicketStats(params);
      setStats(res.data);
    } catch (e) {
      console.error("Failed to fetch stats", e);
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return (
    <div data-testid="dashboard-page">
      {/* Date Range Picker */}
      <div className="mb-6 flex items-center gap-4">
        <DateRangePicker
          dateRange={dateRange}
          onChange={handleDateChange}
        />
        {!dateRange?.isDefault && (
          <button
            onClick={handleResetToWorkWeek}
            className="text-xs text-blue-600 hover:text-blue-800 underline"
          >
            Reset to This Week
          </button>
        )}
      </div>

      <div className="space-y-6 max-w-[1600px] mx-auto" data-testid="dashboard-content">
        {/* KPI Cards Row */}
        <KPICards stats={stats} />

        {/* SLA Alerts - Below KPIs */}
        <SLAAlerts />

        {/* Charts Grid - Bento Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Assigned To - Wide */}
          <div className="lg:col-span-7">
            <AssignedToChart data={stats?.by_assignee} />
          </div>

          {/* Status Donut */}
          <div className="lg:col-span-5">
            <StatusDonut data={stats?.by_status} />
          </div>
        </div>

        {/* Trend Chart - Full Width */}
        <TicketTrendChart data={stats?.trend} />

        {/* Bottom Row - 5 columns including Survey */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Category Breakdown */}
          <div className="lg:col-span-3">
            <CategoryChart data={stats?.by_category} />
          </div>

          {/* Priority Chart */}
          <div className="lg:col-span-2">
            <PriorityChart data={stats?.by_priority} />
          </div>

          {/* AI Quality Scores */}
          <div className="lg:col-span-2">
            <QualityScoreWidget />
          </div>

          {/* Service Surveys */}
          <div className="lg:col-span-2">
            <SurveyStatsWidget />
          </div>

          {/* Recent Activity */}
          <div className="lg:col-span-3">
            <RecentTickets tickets={stats?.recent_tickets} />
          </div>
        </div>

        {/* Footer */}
        <div className="text-center py-4">
          <p className="text-[11px] text-slate-500">
            Gamer Packaging IT Help Desk
          </p>
        </div>
      </div>
    </div>
  );
}
